import { Link } from "react-router-dom";
import { Search, ShoppingBag, BadgeCheck, PackageCheck } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

const STEPS = [
  { icon: Search, n: "01", title: "Browse Products", text: "Explore one of DC's widest selections of premium flower, pre-rolls, edibles, vapes, concentrates and specialty items. Filter by category, price or type to find your perfect match." },
  { icon: ShoppingBag, n: "02", title: "Add Products to Cart", text: "Choose your strains, sizes and quantities. Your cart follows you across the site and updates instantly — review everything before you check out." },
  { icon: BadgeCheck, n: "03", title: "Complete Verification & Checkout", text: "Create your account, verify you're 21+ with a valid photo ID, and complete checkout on our secure platform. It takes less than a minute." },
  { icon: PackageCheck, n: "04", title: "Receive Your Order", text: "Our team confirms your order and dispatches a driver. Fast, discreet delivery to your door in about 30–90 minutes — have your ID ready." },
];

export default function HowToOrder() {
  return (
    <main data-testid="how-to-order-page">
      <Seo
        title="How to Order | Farmacy District DC"
        description="Ordering cannabis delivery in Washington, DC is easy: browse the menu, add to cart, verify you're 21+, and receive your order in 30–90 minutes."
      />
      <section className="bg-brand-ink py-20 text-white grain sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Simple by Design</p>
            <h1 className="mt-4 max-w-2xl font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              How to Order
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
              You're always covered with an efficient delivery network that gets your products delivered in no time.
              Four steps — that's it.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="space-y-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06}>
              <div className="flex flex-col gap-6 border border-neutral-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] sm:flex-row sm:items-center sm:p-10" data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center gap-5 sm:w-64 sm:flex-shrink-0">
                  <span className="font-display text-5xl font-black text-brand-lime">{s.n}</span>
                  <s.icon className="h-8 w-8 text-brand-red" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 text-center">
            <Link
              to="/shop"
              data-testid="how-to-order-shop-cta"
              className="inline-block rounded-full bg-brand-lime px-10 py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
            >
              START YOUR ORDER
            </Link>
            <p className="mt-5 text-xs text-neutral-500">
              Questions? Call <a href="tel:+12029134523" className="font-bold text-brand-ink">(202) 913-4523</a> — our team is here for you.
            </p>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
